import Joi from "joi";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import Toast from "../../helpers/Toast";
import { ICountryDTO } from "../../models/Country/ICountryDTO";
import { ICountryUpdateDTO } from "../../models/Country/ICountryUpdateDTO";
import { CountryService } from "../../services/CountryService";
import useValidationSchema, { FieldConfig } from "../../components/Validations";

export default function CountryEdit() {
    const { id } = useParams<{ id: string }>();
    const intl = useIntl();
    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [country, setCountry] = useState<ICountryDTO>({} as ICountryDTO);
    const countryService = new CountryService();
    const navigate = useNavigate();

    const fields: FieldConfig[] = [
        ["name", "string", "Screens.Country.Validation.NameRequired"],
        ["iso2Code", "string", "Screens.Country.Validation.Iso2CodeRequired"],
        ["iso3Code", "string", "Screens.Country.Validation.Iso3CodeRequired"]
    ];

    const schemaClientValidation = useValidationSchema(fields);

    const getCountry = async () => {
        setLoadingUpdate(true);
        const response = await countryService.GetCountryById(Number(id));
        if (response.success === false || response.obj == null) {
            setLoadingUpdate(false);
            Toast.Show("error", response.message);
            return;
        }
        setCountry({ id: response.obj.id, name: response.obj.name, iso2Code: response.obj.iso2Code, iso3Code: response.obj.iso3Code });
        setLoadingUpdate(false);
    };

    useEffect(() => {
        getCountry();
    }, []);

    const editCountry = async () => {
        setLoadingUpdate(true);

        const updatedCountry: ICountryUpdateDTO = {
            id: Number(id),
            name: country.name,
            iso2Code: country.iso2Code,
            iso3Code: country.iso3Code,
        };

        const { error } = schemaClientValidation.validate(updatedCountry);
        if (error) {
            Toast.Show("error", error.details.map((detail) => detail.message).join(", "));
            setLoadingUpdate(false);
            return;
        }

        const response = await countryService.Edit(updatedCountry);
        setLoadingUpdate(false);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return;
        }
        navigate(-1);
        Toast.Show("success", response.message);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <PageTitle title={intl.formatMessage({ id: "Screens.Country.EditTitle" })} allowGoBack={true} />
                </div>
                <div className="pageBody">
                    <label style={{ margin: "0px", padding: "13px" }}>
                        <b>Dados do País</b>
                    </label>
                    <Row
                        style={{
                            margin: "0px",
                            padding: "0px",
                            alignItems: "flex-end",
                            alignContent: "flex-end",
                        }}
                    >
                        <Row>
                            <InputComponent
                                xl={6}
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={country?.name}
                                label={intl.formatMessage({ id: "Screens.Country.Info.Name" })}
                                readOnly={false}
                                onChange={(t: string) => {
                                    setCountry({ ...country, name: t })
                                }}
                            />
                        </Row>
                        <Row>
                            <InputComponent
                                xl={3}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={country?.iso2Code}
                                label={intl.formatMessage({ id: "Screens.Country.Info.Iso2Code" })}
                                readOnly={false}
                                onChange={(t: string) => {
                                    setCountry({ ...country, iso2Code: t })
                                }}
                            />
                            <InputComponent
                                xl={3}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={country?.iso3Code}
                                label={intl.formatMessage({
                                    id: "Screens.Country.Info.Iso3Code",
                                })}
                                readOnly={false}
                                onChange={(t: string) => {
                                    setCountry({ ...country, iso3Code: t })
                                }}
                            />
                        </Row>
                    </Row>
                    <Row style={{ margin: "0px", marginTop: "10px", padding: "0px" }}>
                        <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                            <ButtonComponent
                                disabled={loadingUpdate}
                                loading={loadingUpdate}
                                text={intl.formatMessage({ id: "CountryEdit.Save" })}
                                onClick={editCountry}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </Layout>
    );
}
